import { useEffect, useState } from 'react';
import Dropdown from 'src/components/atoms/Dropdown';
import RangeSlider from 'src/components/atoms/RangeSlider';
import { getProjectCount } from 'src/services/getProjectCount';
import { DropDownItems } from 'src/store/dropDown/dropDownItemsStore.types';
import { INITIAL_FILTERS } from 'src/store/filters/filtersStore';
import {
  useAction,
  useDropDownItemsStore,
  useLabels,
  useFilterSettingsStore,
  useLanguageStore,
  useWindowWidthStore,
} from 'src/StoreDataProvider';

import { FilterProps } from './filter.types';

const Filter: (props: FilterProps) => JSX.Element = props => {
  const { getDropDownItemsAction, getProjects } = useAction();
  const dropDownItemsState = useDropDownItemsStore();
  const {
    setDropDownItem,
    resetFilters,
    setFiltersRatingMinRatingMax,
    setFilters,
    getReportStartYearAndEndYear,
  } = useAction();
  const windowState = useWindowWidthStore();
  const labels = useLabels();
  const filterSettings = useFilterSettingsStore();
  const {
    ratingMin,
    ratingMax,
    region,
    country,
    sector,
    sector2,
    typeOfProjectPartner,
  } = filterSettings;

  const getFilterItem = (key: string): string[] => {
    const filterItemLookUp: Record<string, string[]> = {
      region,
      country,
      sector,
      sector2,
      typeOfProjectPartner,
      default: [''],
    };
    return filterItemLookUp[key] ?? filterItemLookUp.default;
  };

  const getFiltersChanged = (): boolean => {
    const tempFilterSettings = { ...filterSettings };
    tempFilterSettings.page = INITIAL_FILTERS.page;
    tempFilterSettings.sort = INITIAL_FILTERS.sort;
    tempFilterSettings.size = INITIAL_FILTERS.size;
    return (
      JSON.stringify(tempFilterSettings) !== JSON.stringify(INITIAL_FILTERS)
    );
  };

  const handleShowResults = (): void => {
    props.setShowFilter(!props.showFilter);
    getProjects(filterSettings, language, getReportStartYearAndEndYear());
  };

  const { language, firstLanguageChangeDone } = useLanguageStore();
  const [resultsCount, setResultsCount] = useState(0);

  useEffect(() => {
    if (firstLanguageChangeDone) {
      getDropDownItemsAction(language, getReportStartYearAndEndYear());
    }
  }, [language, firstLanguageChangeDone]);

  useEffect(() => {
    if (windowState.isMobileView && props.showFilter) {
      getProjectCount(filterSettings, language, getReportStartYearAndEndYear())
        .then(value => {
          setResultsCount(value);
        })
        .catch(() => {
          throw new Error('Unable to get the results count');
        });
    }
  }, [filterSettings, windowState.isMobileView]);

  useEffect(() => {
    if (props.showFilter) {
      setFilters({ ...filterSettings });
      window.scrollTo({ top: 0 });
    }
  }, [props.showFilter]);

  return (
    <div
      className="filter-card"
      style={{
        display:
          windowState.isMobileView && !props.showFilter ? 'none' : 'block',
      }}
    >
      {windowState.isMobileView && (
        <div
          className={
            windowState.isMobileView
              ? 'filter-reset-mobile-wrapper'
              : 'filter-reset-desktop'
          }
        >
          <button
            id="filter-reset"
            className="filter-reset-mobile"
            onClick={() => {
              resetFilters();
            }}
            style={{ visibility: getFiltersChanged() ? 'visible' : 'hidden' }}
          >
            <em className="icon-schliessen" aria-hidden="true" />
            <div>{labels.resetFilter}</div>
          </button>
        </div>
      )}
      {Object.entries(dropDownItemsState).map(
        ([key, value]: [string, DropDownItems[]], index) => {
          return (
            <div key={index} className="dropdown-filter">
              <Dropdown
                items={value}
                label={labels[key]}
                id={'DropDown_' + key}
                multiple
                selected={getFilterItem(key)}
                onChange={selected => {
                  setDropDownItem(key, selected);
                }}
              />
            </div>
          );
        }
      )}
      <RangeSlider
        label={labels.overallRating}
        selectedMin={ratingMin}
        selectedMax={ratingMax}
        min={1}
        max={6}
        backgroundColor="white"
        backgroundSelectedColor="#519dc2"
        onChange={(innerSelectedMin, innerSelectedMax) => {
          setFiltersRatingMinRatingMax(innerSelectedMin, innerSelectedMax);
        }}
      ></RangeSlider>
      {windowState.isMobileView && props.showFilter && (
        <button
          className="button-nav button-nav__filter"
          onClick={() => {
            handleShowResults();
          }}
        >
          <em className="icon-pfeil-rechts" aria-hidden="true" />
          <div>
            {`${labels.showResults1} ${
              isNaN(resultsCount)
                ? ''
                : resultsCount > 1000
                ? '1000+'
                : resultsCount
            } ${labels.showResults2}`}
          </div>
        </button>
      )}
      {!windowState.isMobileView && (
        <button
          id="filter-reset"
          className="filter-reset-desktop"
          onClick={() => {
            resetFilters();
          }}
          style={{ visibility: getFiltersChanged() ? 'visible' : 'hidden' }}
        >
          <em className="icon-schliessen" aria-hidden="true" />
          <div>{labels.resetFilter}</div>
        </button>
      )}
    </div>
  );
};

export default Filter;
